import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { dayCareApi } from '@/api/daycare';
import type { DayCareProcedure, DayCareResource } from '@/types/daycare';
import { DayCareResourceType } from '@/types/daycare';
import { Plus, Pencil, Ban } from 'lucide-react';

const RESOURCE_TYPE_LABELS: Record<DayCareResourceType, string> = {
  [DayCareResourceType.OBSERVATION_BED]: 'Observation Bed',
  [DayCareResourceType.PROCEDURE_ROOM]: 'Procedure Room',
  [DayCareResourceType.DIALYSIS_MACHINE]: 'Dialysis Machine',
  [DayCareResourceType.RECLINER]: 'Recliner',
  [DayCareResourceType.ENDOSCOPY_SUITE]: 'Endoscopy Suite',
  [DayCareResourceType.OTHER]: 'Other',
};

export default function DayCareSetupPage() {
  const queryClient = useQueryClient();
  const [procedureDialogOpen, setProcedureDialogOpen] = useState(false);
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<DayCareProcedure | null>(null);
  const [editingResource, setEditingResource] = useState<DayCareResource | null>(null);

  const [procName, setProcName] = useState('');
  const [procDepartmentId, setProcDepartmentId] = useState('');
  const [procDuration, setProcDuration] = useState<string>('');
  const [procBaseCharge, setProcBaseCharge] = useState<string>('');

  const [resName, setResName] = useState('');
  const [resType, setResType] = useState<string>('');
  const [resDepartmentId, setResDepartmentId] = useState('');

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: () => hospitalAdminApi.getDepartments(),
  });

  const { data: procedures = [], isLoading: proceduresLoading } = useQuery({
    queryKey: ['daycare-procedures-admin', 'include-inactive'],
    queryFn: async () => {
      const res = await dayCareApi.listProcedures({ includeInactive: true });
      const raw = res.data as { data?: DayCareProcedure[] } | DayCareProcedure[] | undefined;
      return Array.isArray(raw) ? raw : (raw?.data ?? []);
    },
  });

  const { data: resources = [], isLoading: resourcesLoading } = useQuery({
    queryKey: ['daycare-resources-admin', 'include-inactive'],
    queryFn: async () => {
      const res = await dayCareApi.listResources({ includeInactive: true });
      const raw = res.data as { data?: DayCareResource[] } | DayCareResource[] | undefined;
      return Array.isArray(raw) ? raw : (raw?.data ?? []);
    },
  });

  const createProcedureMutation = useMutation({
    mutationFn: (data: { name: string; departmentId: string; defaultDurationMinutes?: number; baseCharge: number }) =>
      dayCareApi.createProcedure(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-procedures-admin'] });
      queryClient.invalidateQueries({ queryKey: ['daycare-procedures'] });
      toast.success('Procedure added');
      resetProcedureForm();
      setProcedureDialogOpen(false);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to add procedure'),
  });

  const updateProcedureMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof dayCareApi.updateProcedure>[1] }) =>
      dayCareApi.updateProcedure(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-procedures-admin'] });
      queryClient.invalidateQueries({ queryKey: ['daycare-procedures'] });
      toast.success('Procedure updated');
      resetProcedureForm();
      setProcedureDialogOpen(false);
      setEditingProcedure(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to update procedure'),
  });

  const deleteProcedureMutation = useMutation({
    mutationFn: (id: string) => dayCareApi.deleteProcedure(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-procedures-admin'] });
      queryClient.invalidateQueries({ queryKey: ['daycare-procedures'] });
      toast.success('Procedure deactivated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to deactivate procedure'),
  });

  const createResourceMutation = useMutation({
    mutationFn: (data: { name: string; type: string; departmentId: string }) =>
      dayCareApi.createResource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-resources-admin'] });
      queryClient.invalidateQueries({ queryKey: ['daycare-resources'] });
      toast.success('Resource added');
      resetResourceForm();
      setResourceDialogOpen(false);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to add resource'),
  });

  const updateResourceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof dayCareApi.updateResource>[1] }) =>
      dayCareApi.updateResource(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-resources-admin'] });
      queryClient.invalidateQueries({ queryKey: ['daycare-resources'] });
      toast.success('Resource updated');
      resetResourceForm();
      setResourceDialogOpen(false);
      setEditingResource(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to update resource'),
  });

  const deleteResourceMutation = useMutation({
    mutationFn: (id: string) => dayCareApi.deleteResource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daycare-resources-admin'] });
      queryClient.invalidateQueries({ queryKey: ['daycare-resources'] });
      toast.success('Resource deactivated');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to deactivate resource'),
  });

  function resetProcedureForm() {
    setProcName('');
    setProcDepartmentId('');
    setProcDuration('');
    setProcBaseCharge('');
  }

  function resetResourceForm() {
    setResName('');
    setResType('');
    setResDepartmentId('');
  }

  function openAddProcedure() {
    setEditingProcedure(null);
    resetProcedureForm();
    setProcedureDialogOpen(true);
  }

  function openEditProcedure(p: DayCareProcedure) {
    setEditingProcedure(p);
    setProcName(p.name);
    setProcDepartmentId(p.departmentId);
    setProcDuration(p.defaultDurationMinutes != null ? String(p.defaultDurationMinutes) : '');
    setProcBaseCharge(String(p.baseCharge));
    setProcedureDialogOpen(true);
  }

  function openAddResource() {
    setEditingResource(null);
    resetResourceForm();
    setResourceDialogOpen(true);
  }

  function openEditResource(r: DayCareResource) {
    setEditingResource(r);
    setResName(r.name);
    setResType(r.type);
    setResDepartmentId(r.departmentId);
    setResourceDialogOpen(true);
  }

  function submitProcedure(e: React.FormEvent) {
    e.preventDefault();
    const baseCharge = Number(procBaseCharge);
    if (!procName.trim()) {
      toast.error('Procedure name is required');
      return;
    }
    if (!procDepartmentId) {
      toast.error('Department is required');
      return;
    }
    if (Number.isNaN(baseCharge) || baseCharge < 0) {
      toast.error('Valid base charge is required');
      return;
    }
    const payload = {
      name: procName.trim(),
      departmentId: procDepartmentId,
      defaultDurationMinutes: procDuration ? Number(procDuration) : undefined,
      baseCharge,
    };
    if (editingProcedure) {
      updateProcedureMutation.mutate({
        id: editingProcedure.id,
        data: { ...payload, isActive: editingProcedure.isActive },
      });
    } else {
      createProcedureMutation.mutate(payload);
    }
  }

  function submitResource(e: React.FormEvent) {
    e.preventDefault();
    if (!resName.trim()) {
      toast.error('Resource name is required');
      return;
    }
    if (!resType) {
      toast.error('Resource type is required');
      return;
    }
    if (!resDepartmentId) {
      toast.error('Department is required');
      return;
    }
    const payload = { name: resName.trim(), type: resType, departmentId: resDepartmentId };
    if (editingResource) {
      updateResourceMutation.mutate({
        id: editingResource.id,
        data: { ...payload, isActive: editingResource.isActive },
      });
    } else {
      createResourceMutation.mutate(payload);
    }
  }

  const departmentName = (id: string) => departments?.find((d) => d.id === id)?.name ?? id;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tabular-nums text-artis-brown-dark">Day Care Setup</h1>
        <p className="thms-page-subtitle">
          Manage procedures and resources used for Day Care cases. These appear when starting a Day Care case or sending from consultation.
        </p>
      </div>

      <Tabs defaultValue="procedures" className="space-y-4">
        <TabsList>
          <TabsTrigger value="procedures">Procedures</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>

        <TabsContent value="procedures" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Procedures</CardTitle>
              <Button onClick={openAddProcedure} className="bg-blue-800 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Procedure
              </Button>
            </CardHeader>
            <CardContent>
              {proceduresLoading ? (
                <p className="text-gray-500 py-4">Loading procedures...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Duration (min)</TableHead>
                      <TableHead>Base charge (₹)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {procedures.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                          No procedures. Add one to use in Day Care cases.
                        </TableCell>
                      </TableRow>
                    ) : (
                      procedures.map((p) => (
                        <TableRow key={p.id} className={!p.isActive ? 'opacity-60' : ''}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell>{departmentName(p.departmentId)}</TableCell>
                          <TableCell>{p.defaultDurationMinutes ?? '—'}</TableCell>
                          <TableCell>{p.baseCharge}</TableCell>
                          <TableCell>
                            <span className={p.isActive ? 'text-green-600' : 'text-gray-500'}>
                              {p.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditProcedure(p)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              {p.isActive && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteProcedureMutation.mutate(p.id)}
                                  disabled={deleteProcedureMutation.isPending}
                                >
                                  <Ban className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Resources</CardTitle>
              <Button onClick={openAddResource} className="bg-blue-800 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Resource
              </Button>
            </CardHeader>
            <CardContent>
              {resourcesLoading ? (
                <p className="text-gray-500 py-4">Loading resources...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resources.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                          No resources. Add one to assign to Day Care cases (e.g. procedure room, bed).
                        </TableCell>
                      </TableRow>
                    ) : (
                      resources.map((r) => (
                        <TableRow key={r.id} className={!r.isActive ? 'opacity-60' : ''}>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell>{RESOURCE_TYPE_LABELS[r.type as DayCareResourceType] ?? r.type}</TableCell>
                          <TableCell>{departmentName(r.departmentId)}</TableCell>
                          <TableCell>
                            <span className={r.isActive ? 'text-green-600' : 'text-gray-500'}>
                              {r.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openEditResource(r)}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              {r.isActive && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteResourceMutation.mutate(r.id)}
                                  disabled={deleteResourceMutation.isPending}
                                >
                                  <Ban className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={procedureDialogOpen} onOpenChange={setProcedureDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProcedure ? 'Edit Procedure' : 'Add Procedure'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitProcedure} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={procName} onChange={(e) => setProcName(e.target.value)} placeholder="e.g. Dialysis Session" required />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={procDepartmentId} onValueChange={setProcDepartmentId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments?.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default duration (minutes, optional)</Label>
              <Input type="number" min={0} value={procDuration} onChange={(e) => setProcDuration(e.target.value)} placeholder="e.g. 60" />
            </div>
            <div className="space-y-2">
              <Label>Base charge (₹)</Label>
              <Input type="number" min={0} step={0.01} value={procBaseCharge} onChange={(e) => setProcBaseCharge(e.target.value)} placeholder="0" required />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setProcedureDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createProcedureMutation.isPending || updateProcedureMutation.isPending}>
                {editingProcedure ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingResource ? 'Edit Resource' : 'Add Resource'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitResource} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={resName} onChange={(e) => setResName(e.target.value)} placeholder="e.g. Room 1" required />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={resType} onValueChange={setResType} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(RESOURCE_TYPE_LABELS) as DayCareResourceType[]).map((t) => (
                    <SelectItem key={t} value={t}>{RESOURCE_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={resDepartmentId} onValueChange={setResDepartmentId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments?.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setResourceDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createResourceMutation.isPending || updateResourceMutation.isPending}>
                {editingResource ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
